import React, {FC, useEffect, useRef, useState} from "react";
import classNames from "classnames";
import FlexBox from "view/components/Layout/FlexBox";
import html2canvas from "html2canvas";
import {RawAttachmentFile} from "ts/api/attachmentFile";
import {v4 as uuidv4} from "uuid";
import Alert from "view/components/Alert";
import {ISignature} from "@evennt-tech/api-v2-core";
import ColorPicker from "../ColorPicker";
import useStyles from "./styles";
import FilePreview from "../FilePreview";
import FontOptions from "./FontOptions";
import {Colors, FontFamily} from "../../constants";

type Props = {
  penColor: string;
  setPenColor: (color: string) => void;
  setTypedFile: (file: RawAttachmentFile | null) => void;
  signatureText: string;
  setSignatureText: React.Dispatch<any>;
  signatureToEdit?: ISignature;
  showCanvas: boolean;
  setShowCanvas: React.Dispatch<React.SetStateAction<boolean>>;
  style?: React.CSSProperties;
};

const fontOptions = [
  {name: FontFamily.Playball, fontFamily: `${FontFamily.Playball} !important`},
  {
    name: FontFamily.MarckScript,
    fontFamily: `${FontFamily.MarckScript} !important`,
  },
  {
    name: FontFamily.Noteworthy,
    fontFamily: `${FontFamily.Noteworthy} !important`,
  },
  {name: FontFamily.Sofia, fontFamily: `${FontFamily.Sofia} !important`},
];

const title = "signature";
const defaultText = "Create signature";
const maxCharacters = 75;

const TypeSignature: FC<Props> = ({
  penColor,
  setPenColor,
  setTypedFile,
  signatureText,
  setSignatureText,
  signatureToEdit,
  showCanvas,
  setShowCanvas,
  style,
}) => {
  const classes = useStyles();
  const textRef = useRef<HTMLDivElement>(null);

  const [selectedFont, setSelectedFont] = useState(fontOptions[0].fontFamily);
  const [typingTimeout, setTypingTimeout] = useState<NodeJS.Timeout | null>(
    null
  );
  const [showAlert, setShowAlert] = useState(false);

  useEffect(() => {
    const fontLinks = [
      "https://fonts.googleapis.com/css2?family=Playball&display=swap",
      "https://fonts.googleapis.com/css2?family=Marck+Script&display=swap",
      "https://fonts.googleapis.com/css2?family=Noteworthy&display=swap",
      "https://fonts.googleapis.com/css2?family=Sofia&display=swap",
    ];

    fontLinks.forEach((href) => {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = href;
      document.head.appendChild(link);
    });

    return () => {
      fontLinks.forEach((href) => {
        const link = document.querySelector(`link[href="${href}"]`);
        if (link) {
          document.head.removeChild(link);
        }
      });
    };
  }, []);

  const fontsMap = {
    [FontFamily.MarckScript]: classes.fontMarckScript,
    [FontFamily.Noteworthy]: classes.fontNoteworthy,
    [FontFamily.Playball]: classes.fontPlayball,
    [FontFamily.Sofia]: classes.fontSofia,
  } as const;
  const colorsMap = {
    [Colors.Black]: classes.colorBlack,
    [Colors.DarkBlue]: classes.colorDarkBlue,
    [Colors.Blue]: classes.colorBlue,
  } as const;

  const handleTextChange = (event: React.FormEvent<HTMLDivElement>) => {
    let updatedText = (event.currentTarget as HTMLDivElement).textContent || "";
    if (updatedText.length > maxCharacters) {
      updatedText = updatedText.substring(0, maxCharacters);
      setShowAlert(true);

      if (textRef.current) {
        textRef.current.textContent = updatedText;
      }
    } else {
      setShowAlert(false);
    }
    setSignatureText(updatedText);

    if (typingTimeout) {
      clearTimeout(typingTimeout);
    }

    const newTimeout = setTimeout(() => {
      handleSetTypeFile();
    }, 1000);
    setTypingTimeout(newTimeout);

    const range = document.createRange();
    const sel = window.getSelection();
    if (textRef.current && sel) {
      range.selectNodeContents(textRef.current);
      range.collapse(false);
      sel.removeAllRanges();
      sel.addRange(range);
    }
  };
  const handleFontChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedFont(event.target.value);
    handleSetTypeFile();
  };
  const handleColorChange = (color: string) => {
    setPenColor(color);
    handleSetTypeFile();
  };
  const handleSetTypeFile = async () => {
    await document.fonts.ready;

    if (textRef.current) {
      const canvas = await html2canvas(textRef.current, {
        backgroundColor: null,
        // Capture at ≥3× so the baked PNG stays crisp when the PDF scales it
        // up (signatureImage renders at 84×150). Default scale is the
        // device pixel ratio (often 1), which is why it looked pixelated.
        scale: Math.max(window.devicePixelRatio || 1, 3),
      });
      canvas.toBlob((blob) => {
        if (blob) {
          const file = new File([blob], title, {
            type: "image/png",
            lastModified: Date.now(),
          });
          const attachmentFile: RawAttachmentFile = {
            id: uuidv4(),
            title,
            file,
          };
          setTypedFile(attachmentFile);
        }
      });
    }
  };
  const handleRemoveFile = () => {
    setTypedFile(null);
    setShowCanvas(true);
  };

  const getFontClass = () => {
    const cleanedFont = selectedFont.replace(
      " !important",
      ""
    ) as keyof typeof fontsMap;
    return fontsMap[cleanedFont] || "";
  };
  const getColorClass = () =>
    colorsMap[penColor as keyof typeof colorsMap] || "";
  const getDefaultTextClass = () => {
    return signatureText === defaultText ? classes.defaultText : "";
  };

  if (signatureToEdit?.file && !showCanvas) {
    return (
      <FilePreview
        file={signatureToEdit.file}
        handleRemoveFile={handleRemoveFile}
      />
    );
  }

  return (
    <>
      <ColorPicker penColor={penColor} setPenColor={handleColorChange} />
      <FlexBox
        component="div"
        alignCenter
        justifyCenter
        className={classes.typedBox}
        style={{
          backgroundColor: style?.backgroundColor
            ? style.backgroundColor
            : "white",
        }}
      >
        <div
          id="type-signature-input-editable"
          contentEditable
          suppressContentEditableWarning
          ref={textRef}
          onInput={handleTextChange}
          onFocus={() => {
            if (signatureText === defaultText) {
              setSignatureText("");
            }
          }}
          onBlur={() => {
            if (signatureText.trim() === "") {
              setSignatureText(defaultText);
            } else {
              handleSetTypeFile();
            }
          }}
          className={classNames(
            classes.div,
            getFontClass(),
            getColorClass(),
            getDefaultTextClass()
          )}
          style={{width: signatureText ? "fit-content" : "100%"}}
        >
          {signatureText}
        </div>
      </FlexBox>

      {showAlert && (
        <Alert severity="error" className={classes.alert}>
          Character limit exceeded
        </Alert>
      )}

      <FlexBox justifyContent="space-between" className={classes.signatureBox}>
        <FlexBox column style={{width: "50%"}}>
          <FontOptions
            fonts={fontOptions.slice(0, 2)}
            selectedFont={selectedFont}
            handleFontChange={handleFontChange}
            signatureText={signatureText}
            fontsMap={fontsMap}
          />
        </FlexBox>
        <FlexBox column style={{width: "50%"}}>
          <FontOptions
            fonts={fontOptions.slice(2)}
            selectedFont={selectedFont}
            handleFontChange={handleFontChange}
            signatureText={signatureText}
            fontsMap={fontsMap}
          />
        </FlexBox>
      </FlexBox>
    </>
  );
};

export default TypeSignature;
